// The wall's own headline and text (D-098), in the Design tab: the headline, the text under it,
// the tag us line and a button, where they sit and how they look, written per language.
import {
  copyFor,
  isInstagramLink,
  LANG_NAMES,
  WALL_LANGS,
  type Wall,
  type WallCopy,
  type WallCopyPatch,
  type WallLang,
  type WallText,
  wallWords,
} from "@brandwall/contracts";

type Draft =
  | (Partial<Omit<WallText, "copy">> & { copy?: WallCopyPatch; dismissSuggestion?: boolean })
  | null;

const DEFAULT_HEADING = "Seen on you";

export function HeadlineAndText(p: {
  wall: Wall;
  /** The headline's on/off and main wording, kept in the design. */
  heading: string;
  setHeading: (h: string) => void;
  /** The words with unsaved changes applied. */
  text: WallText;
  draft: Draft;
  setDraft: (d: Draft | ((d: Draft) => Draft)) => void;
  /** The language being written in, the same as the preview's. */
  lang: WallLang;
  /** Null when the wall always speaks one language. */
  setLang: ((l: WallLang) => void) | null;
  tag: { account: string | null; hashtag: string | null };
  disabled: boolean;
  /** The link typed isn't a full web address. */
  badButtonUrl: boolean;
  dismiss: () => void;
}) {
  const { text: t, lang, wall } = p;
  const brand = wall.brandLanguage;
  const words = wallWords(lang);
  const change = (fields: Partial<Omit<WallText, "copy">>) =>
    p.setDraft((d) => ({ ...(d ?? {}), ...fields }));
  const write = (part: keyof WallCopy, value: string) =>
    p.setDraft((d) => ({
      ...(d ?? {}),
      copy: { ...(d?.copy ?? {}), [lang]: { ...(d?.copy?.[lang] ?? {}), [part]: value } },
    }));
  const own = (part: keyof WallCopy) => t.copy[lang]?.[part] ?? "";
  // What shows when this language has no words of its own.
  const fallback = (part: keyof WallCopy) =>
    lang === brand ? null : (copyFor(t, part, brand, brand) ?? null);
  const headingFallback =
    fallback("heading") ?? (p.heading === DEFAULT_HEADING ? words.seenOnYou : p.heading);
  const s = wall.suggestion;
  const tagExample =
    p.tag.account && p.tag.hashtag
      ? words.tagUsOr.replace("{account}", p.tag.account).replace("{hashtag}", p.tag.hashtag)
      : p.tag.account || p.tag.hashtag
        ? words.tagUs.replace("{account}", (p.tag.account ?? p.tag.hashtag)!)
        : null;

  return (
    <fieldset className="card stack" disabled={p.disabled} style={{ margin: 0 }}>
      <legend style={{ fontWeight: 700, padding: 0 }}>Headline and text</legend>
      <p className="small muted" style={{ margin: 0 }}>
        Words written here hide together with the wall and show in your shopper's language, so write
        them here rather than in your page.
      </p>
      {s && !p.draft?.dismissSuggestion && (
        <div className="note stack" style={{ gap: 6 }} role="note">
          <span className="small muted">Suggested for this wall, in your voice:</span>
          <b>{s.heading}</b>
          <span>{s.intro}</span>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                const l = s.lang ?? brand;
                if (!p.heading) p.setHeading(DEFAULT_HEADING);
                p.setDraft((d) => ({
                  ...(d ?? {}),
                  intro: true,
                  dismissSuggestion: true,
                  copy: {
                    ...(d?.copy ?? {}),
                    [l]: { ...(d?.copy?.[l] ?? {}), heading: s.heading, intro: s.intro },
                  },
                }));
              }}
            >
              Use it
            </button>
            <button type="button" className="btn link small" onClick={p.dismiss}>
              No thanks
            </button>
          </div>
        </div>
      )}
      <div className="row wrap" style={{ gap: 8 }}>
        <span className="spacer small muted">
          Writing in {LANG_NAMES[lang]}
          {lang === brand
            ? ", your own language. Other languages use these words until they have their own."
            : "."}
        </span>
        {p.setLang && (
          <select
            className="input"
            style={{ width: "auto" }}
            aria-label="Write in"
            value={lang}
            onChange={(e) => p.setLang?.(e.target.value as WallLang)}
          >
            {WALL_LANGS.map((l) => (
              <option key={l} value={l}>
                {LANG_NAMES[l]}
              </option>
            ))}
          </select>
        )}
      </div>

      <label className="row" style={{ gap: 8 }}>
        <input
          type="checkbox"
          checked={Boolean(p.heading)}
          onChange={(e) => p.setHeading(e.target.checked ? DEFAULT_HEADING : "")}
        />
        <b>Headline</b>
      </label>
      {p.heading && (
        <input
          className="input"
          aria-label="Headline"
          maxLength={120}
          value={own("heading")}
          placeholder={headingFallback}
          onChange={(e) => write("heading", e.target.value)}
        />
      )}

      <label className="row" style={{ gap: 8 }}>
        <input
          type="checkbox"
          checked={t.intro}
          onChange={(e) => change({ intro: e.target.checked })}
        />
        <b>Text under the headline</b>
      </label>
      {t.intro && (
        <textarea
          className="input"
          aria-label="Text under the headline"
          rows={2}
          maxLength={300}
          value={own("intro")}
          placeholder={
            fallback("intro") ?? "Real families, real rides. Share yours and you might end up here."
          }
          onChange={(e) => write("intro", e.target.value)}
        />
      )}

      <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
        <input
          type="checkbox"
          checked={t.tagUs}
          onChange={(e) => change({ tagUs: e.target.checked })}
          style={{ marginTop: 4 }}
        />
        <span className="stack" style={{ gap: 2 }}>
          <b>Tag us line</b>
          <span className="small muted">
            {tagExample
              ? `"${tagExample}", with links to Instagram.`
              : "Connect your Instagram account or follow a hashtag, and the line shows them."}
          </span>
        </span>
      </label>

      <label className="row" style={{ gap: 8 }}>
        <input
          type="checkbox"
          checked={t.button}
          onChange={(e) => change({ button: e.target.checked })}
        />
        <b>Button</b>
      </label>
      {t.button && (
        <div className="row wrap" style={{ gap: 8 }}>
          <input
            className="input"
            aria-label="Button text"
            maxLength={40}
            style={{ flex: "1 1 160px" }}
            value={own("button")}
            placeholder={
              fallback("button") ??
              (!t.buttonUrl.trim() || isInstagramLink(t.buttonUrl.trim())
                ? words.followUs
                : 'The button\'s words, like "See the bikes"')
            }
            onChange={(e) => write("button", e.target.value)}
          />
          <input
            className="input"
            aria-label="Button link"
            type="url"
            maxLength={500}
            style={{ flex: "2 1 220px" }}
            value={t.buttonUrl}
            placeholder={
              p.tag.account ? `https://www.instagram.com/${p.tag.account.slice(1)}/` : "https://"
            }
            aria-invalid={p.badButtonUrl || undefined}
            onChange={(e) => change({ buttonUrl: e.target.value })}
          />
          <span className="small muted" style={{ flexBasis: "100%" }}>
            {p.badButtonUrl
              ? "Use a full web address, starting with https://."
              : t.buttonUrl.trim()
                ? isInstagramLink(t.buttonUrl.trim())
                  ? "Leave the words empty for the usual ones."
                  : "A link of your own needs the button's words."
                : p.tag.account
                  ? `Without a link of your own, the button opens your Instagram, ${p.tag.account}.`
                  : "Add the link the button opens."}
          </span>
        </div>
      )}

      <div className="row wrap" style={{ gap: 8 }}>
        <div className="seg" role="group" aria-label="Where the text sits">
          {(
            [
              ["above", "Above the posts"],
              ["below", "Below the posts"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={t.place === v}
              onClick={() => change({ place: v })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="seg" role="group" aria-label="Alignment">
          {(
            [
              ["left", "Left"],
              ["center", "Centred"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={t.align === v}
              onClick={() => change({ align: v })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="seg" role="group" aria-label="Style of the words">
        {(
          [
            ["site", "Your website's style"],
            ["wall", "The wall's font and colours"],
          ] as const
        ).map(([v, label]) => (
          <button
            key={v}
            type="button"
            aria-pressed={t.style === v}
            onClick={() => change({ style: v })}
          >
            {label}
          </button>
        ))}
      </div>
      <span className="small muted">
        {t.style === "site"
          ? "On your site the headline and text take your website's own heading and text style. The preview here uses the wall's."
          : "The headline and text use the wall's font and colours from above."}{" "}
        The headline always sits above the posts.
      </span>
    </fieldset>
  );
}
